import React from 'react'
import { Avatar, Slider, SliderThumb } from '@mui/material'
import { ArrowLeft, ArrowRight } from '@mui/icons-material'
import { alpha, styled } from '@mui/system'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import {
  isGanttSummaryVisualType,
  GanttBarVisualType,
  getGanttBarHeight,
  getBarColorByType,
  getShapeStyle,
  buildArrowClipPath,
} from '../ganttUtil'
import { GanttDimensions } from './constants'

// ---------- Color constants ----------

const SliderColorLegacy = {
  TASK: '#888888',
  NOT_SCHEDULED: '#cccccc',
  ANCESTORS: '#686868',
} as const

const SliderColorsV2 = {
  NOT_SCHEDULED: colorPalette.monotone[3],
} as const

// MUI Slider root height:4px, padding:0 → track中心 = root top + 2px
export const SLIDER_TRACK_CENTER = 2

// ---------- Types ----------

export type StyledSliderProps = {
  scheduled: number
  visualtype: GanttBarVisualType
  disablethumbs: boolean[]
  index: number
  isv2?: number
  overrunleft?: number
  overrunwidth?: number
  arrowoverlap?: number
  wbsitemdone?: number
  arrowwidth?: number
}

type SliderInjectedState = {
  value: number[]
  min: number
  max: number
}

type StyledSliderThumbProps = {
  disabled?: boolean
  labeldisabled?: number
  thumb?: number
}

// ---------- Helpers ----------

export function getSliderColor(props: StyledSliderProps) {
  if (props.isv2) {
    if (!props.scheduled) return SliderColorsV2.NOT_SCHEDULED
    return getBarColorByType(props.visualtype, !!props.wbsitemdone)
  }
  const colors = SliderColorLegacy
  if (!props.scheduled) return colors.NOT_SCHEDULED
  const isTask =
    props.visualtype === 'task' || props.visualtype === 'parentTask'
  if (!isTask) return colors.ANCESTORS
  return colors.TASK
}

function computeThumbHitArea(params: {
  transparentThumb: boolean
  sliderValue?: number[]
  sliderMin: number
  sliderMax: number
}): { size: number; innerOffset: number } {
  const { transparentThumb, sliderValue, sliderMin, sliderMax } = params
  const outerOverflow = 2
  const baseSize = transparentThumb ? 28 : 24
  const barRatio =
    sliderValue && sliderMax > sliderMin
      ? (sliderValue[1] - sliderValue[0]) / (sliderMax - sliderMin)
      : 1
  const estimatedBarPx = Math.max(1, barRatio * 800)
  const size = Math.max(
    14,
    Math.min(baseSize, estimatedBarPx + 2 * outerOverflow)
  )
  const innerOffset = size / 2 - outerOverflow
  return { size, innerOffset }
}

// ---------- Layout ----------

export const SliderWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
})

export const ProgressGauge = styled('div')<{ barHeight: number }>(
  ({ barHeight }) => ({
    position: 'absolute',
    height: `${barHeight}px`,
    top: `${SLIDER_TRACK_CENTER}px`,
    transform: 'translateY(-50%)',
    borderRadius: '2px',
    zIndex: 1,
    pointerEvents: 'none' as const,
  })
)

// ---------- Label base ----------

const labelBase = {
  position: 'absolute' as const,
  top: `${SLIDER_TRACK_CENTER}px`,
  transform: 'translateY(-50%)',
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  gap: '3px',
  pointerEvents: 'all' as const,
  whiteSpace: 'nowrap' as const,
  zIndex: 2,
  cursor: 'default',
}

export const InsideLabel = styled('div')<{
  left: number
  width: number
  isSummary?: boolean
}>(({ left, width, isSummary }) => ({
  ...labelBase,
  left: `${left}%`,
  width: `${width}%`,
  height: isSummary ? '26px' : '20px',
  padding: isSummary ? '0 2px' : '0 4px',
  overflow: 'hidden',
  cursor: isSummary ? 'pointer' : 'default',
  pointerEvents: isSummary ? 'none' : 'all',
}))

export const RightLabel = styled('div', {
  shouldForwardProp: prop => prop !== 'centerOnLeft',
})<{
  left: number
  isSummary?: boolean
  maxWidth: string
  centerOnLeft?: boolean
}>(({ left, isSummary, maxWidth, centerOnLeft }) => ({
  ...labelBase,
  left: `${left}%`,
  height: isSummary ? '24px' : '20px',
  padding: '0 6px',
  marginLeft: centerOnLeft
    ? '4px'
    : `${
        GanttDimensions.CONNECTION_CIRCLE_OFFSET +
        GanttDimensions.CONNECTION_CIRCLE_SIZE +
        3
      }px`,
  transform: 'translateY(-50%)',
  backgroundColor: colorPalette.blue[0] + 'E6',
  borderRadius: '4px',
  maxWidth: maxWidth,
  overflow: 'hidden',
  zIndex: centerOnLeft ? 100 : 2,
}))

export const SummaryProgressRate = styled('div')<{ left: number }>(
  ({ left }) => ({
    ...labelBase,
    left: `${left}%`,
    height: '24px',
    padding: '0 6px',
    marginLeft: '4px',
    backgroundColor: colorPalette.blue[0] + 'E6',
    borderRadius: '4px',
  })
)

export const BarHoverArea = styled('div')<{
  left: number
  width: number
  isSummary?: boolean
}>(({ left, width, isSummary }) => ({
  position: 'absolute',
  top: `${SLIDER_TRACK_CENTER}px`,
  transform: 'translateY(-50%)',
  left: `${left}%`,
  width: `${width}%`,
  height: isSummary ? '26px' : '20px',
  cursor: isSummary ? 'pointer' : 'default',
  zIndex: isSummary ? 1 : 2,
}))

// ---------- Label content ----------

export const StatusIcon = styled('div')<{
  color: string
}>(({ color }) => ({
  width: '10px',
  height: '10px',
  minWidth: '10px',
  borderRadius: '50%',
  backgroundColor: color,
  border: `1px solid ${alpha(colorPalette.monotone[10], 0.13)}`,
  pointerEvents: 'none',
  flexShrink: 0,
}))

export const TypeIcon = styled('img')({
  width: '16px',
  height: '16px',
  minWidth: '16px',
  pointerEvents: 'none',
  flexShrink: 0,
})

export const AssigneeAvatar = styled(Avatar)<{ isSummary?: boolean }>(
  ({ isSummary }) => ({
    width: isSummary ? '20px' : '16px',
    height: isSummary ? '20px' : '16px',
    minWidth: isSummary ? '20px' : '16px',
    fontSize: isSummary ? '12px' : '10px',
    marginLeft: '1px',
    color: colorPalette.monotone[10],
    pointerEvents: 'all',
    cursor: 'default',
    flexShrink: 0,
    fontWeight: 'normal',
  })
)

export const NameLabel = styled('span')<{
  inside?: boolean
  isSummary?: boolean
}>(({ inside, isSummary }) => ({
  fontSize: isSummary ? '12px' : '10px',
  fontWeight: 'bold',
  lineHeight: isSummary ? '20px' : '16px',
  color: inside ? colorPalette.monotone[0] : colorPalette.monotone[6],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}))

export const ProgressRateLabel = styled('span')<{
  inside?: boolean
  isSummary?: boolean
  showSeparator?: boolean
}>(({ inside, isSummary, showSeparator = true }) => ({
  fontSize: isSummary ? '14px' : '13px',
  fontWeight: 'bold',
  marginLeft: showSeparator ? '2px' : 0,
  paddingLeft: showSeparator ? '5px' : 0,
  borderLeft: showSeparator
    ? inside
      ? `1px solid ${alpha(colorPalette.monotone[0], 0.4)}`
      : `1px solid ${colorPalette.monotone[3]}`
    : 'none',
  color: inside ? colorPalette.monotone[0] : colorPalette.monotone[6],
  pointerEvents: 'none',
  flexShrink: 0,
}))

// ---------- Slider ----------

export const StyledSlider = styled(Slider)<StyledSliderProps>(({
  scheduled,
  visualtype,
  disablethumbs,
  index,
  isv2,
  overrunleft,
  overrunwidth,
  arrowoverlap,
  wbsitemdone,
  arrowwidth,
}) => {
  const sliderColor = getSliderColor({
    scheduled,
    visualtype,
    disablethumbs,
    index,
    isv2,
    overrunleft,
    overrunwidth,
    wbsitemdone,
  })

  if (isv2) {
    const isSummary = isGanttSummaryVisualType(visualtype)
    const barHeight = getGanttBarHeight(visualtype)
    const shape = getShapeStyle(visualtype)
    const useSvgOverlay = shape === 'line-triangle' || shape === 'bar-triangle'
    const isPill = shape === 'pill'
    const arrowClipPath =
      shape === 'arrow' && arrowwidth
        ? buildArrowClipPath(arrowwidth)
        : undefined
    return {
      height: '4px',
      padding: 0, // MUI size=small の padding:13px を除去し、track位置を予測可能にする
      overflow: 'visible', // barHeight がroot(4px)を超えても描画されるようにする
      width: '100%',
      display: 'inline-block',
      verticalAlign: 'top', // baseline だと親コンテナ内で下にずれるため
      position: 'relative',
      touchAction: 'none',
      pointerEvents: 'none',
      margin: 0,
      '& .MuiSlider-root': {
        color: sliderColor,
      },
      '& .MuiSlider-rail': {
        height: 0,
      },
      '& .MuiSlider-track': {
        display: 'block',
        position: 'absolute',
        pointerEvents: disablethumbs.some(v => !!v) ? 'none' : 'all',
        cursor: 'pointer',
        height: `${isSummary ? 26 : 20}px`,
        backgroundColor: 'transparent',
      },
      '& .MuiSlider-track::after': {
        content: '""',
        position: 'absolute',
        opacity: wbsitemdone ? 1.0 : 0.85,
        transition: 'all 0.1s ease !important',
        width: '100%',
        height: `${barHeight}px`,
        backgroundColor:
          useSvgOverlay || !scheduled ? 'transparent' : sliderColor,
        backgroundImage: !scheduled
          ? `repeating-linear-gradient(-90deg, transparent, transparent 2px, ${sliderColor} 2px, ${sliderColor} 5px)`
          : undefined,
        top: '50%',
        transform: 'translateY(-50%)',
        borderRadius: isPill ? `${barHeight / 2}px` : '2px',
        clipPath: scheduled ? arrowClipPath : undefined,
      },
      '& .MuiSlider-valueLabel:before': {
        bottom: index === 0 ? undefined : 0,
        top: index === 0 ? '-8px' : undefined,
      },
      '&::after': overrunwidth
        ? {
            content: '""',
            position: 'absolute',
            left: `${overrunleft}%`,
            width: `${overrunwidth}%`,
            height: `${barHeight}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            background: `repeating-linear-gradient(45deg, ${colorPalette.yellow[1]}40, ${colorPalette.yellow[1]}40 4px, ${colorPalette.yellow[5]}60 4px, ${colorPalette.yellow[5]}60 5px)`,
            boxSizing: 'border-box',
            borderRadius: arrowoverlap ? undefined : '0 2px 2px 0',
            clipPath: arrowoverlap
              ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${((arrowoverlap / overrunwidth) * 100).toFixed(2)}% 50%)`
              : undefined,
            pointerEvents: 'none',
            zIndex: 0,
          }
        : {},
    }
  }

  // Legacy styles
  const isWorkGroupOrProcess =
    visualtype === 'workgroup' || visualtype === 'process'
  const isArrow = visualtype === 'summary' || visualtype === 'deliverable'
  const isTask = visualtype === 'task' || visualtype === 'parentTask'
  return {
    height: '4px',
    width: '100%',
    display: 'inline-block',
    position: 'relative',
    touchAction: 'none',
    pointerEvents: 'none',
    margin: 0,
    zIndex: 3,
    '& .MuiSlider-root': {
      color: sliderColor,
    },
    '& .MuiSlider-rail': {
      height: 0,
    },
    '& .MuiSlider-track': {
      display: 'block',
      position: 'absolute',
      pointerEvents: disablethumbs.some(v => !!v) ? 'none' : 'all',
      cursor: 'pointer',
      height: `10px`,
      backgroundColor: 'transparent',
    },
    '& .MuiSlider-track::after': {
      content: '""',
      position: 'absolute',
      opacity: isTask ? 0.75 : 1,
      transition: 'all 0.1s ease !important',
      width: '100%',
      height: `${isWorkGroupOrProcess || isArrow ? 10 : 3}px`,
      backgroundColor: !scheduled ? 'transparent' : sliderColor,
      backgroundImage: !scheduled
        ? `repeating-linear-gradient(-90deg, transparent, transparent 2px, ${sliderColor} 2px, ${sliderColor} 5px)`
        : undefined,
      transform: `translateY(${isWorkGroupOrProcess || isArrow ? 1 : 3.5}px)`,
      borderRadius: isWorkGroupOrProcess ? 1 : 'inherit',
      clipPath:
        isWorkGroupOrProcess && !disablethumbs[1]
          ? 'polygon(calc(100% - 4px) 100%, 100% calc(50% + 0.5px), 100% calc(50% - 0.5px), calc(100% - 4px) 0, 0 0, 0 100%)'
          : isArrow && !disablethumbs[1]
            ? 'polygon(0 3.5px, calc(100% - 5px) 3.5px, calc(100% - 5px) 0, 100% 50%, calc(100% - 5px) 100%, calc(100% - 5px) calc(100% - 3.5px), 0 calc(100% - 3.5px))'
            : isArrow && disablethumbs[1]
              ? 'polygon(0 3.5px, 100% 3.5px, 100% calc(100% - 3.5px), 0 calc(100% - 3.5px))'
              : undefined,
    },
    '& .MuiSlider-valueLabel:before': {
      bottom: index === 0 ? undefined : 0,
      top: index === 0 ? '-8px' : undefined,
    },
  }
})

// ---------- Slider Thumb ----------

const StyledSliderThumbBase = styled(SliderThumb)<StyledSliderThumbProps>(
  props => {
    const { thumb, disabled, labeldisabled } = props
    const ownerState = props['ownerState'] as StyledSliderProps &
      Partial<SliderInjectedState>
    const { visualtype, index, isv2, wbsitemdone } = ownerState
    const sliderColor = getSliderColor(ownerState)
    const transparentThumb = !!(
      isv2 ||
      visualtype === 'workgroup' ||
      visualtype === 'process' ||
      ((visualtype === 'summary' || visualtype === 'deliverable') &&
        thumb === 1)
    )
    const resolvedBarColor =
      transparentThumb && !disabled
        ? isv2
          ? getBarColorByType(visualtype, !!wbsitemdone)
          : sliderColor
        : null
    const rowHoverThumbStyle =
      isv2 && resolvedBarColor
        ? {
            '.ag-cell:hover &::after': {
              backgroundColor: colorPalette.monotone[0],
              border: `1.5px solid ${resolvedBarColor}`,
              boxSizing: 'border-box' as const,
              opacity: 0.7,
            },
          }
        : {}
    const topValue = '50%'
    const hoverStyle = {
      boxShadow: `0 0 0 0.25rem ${alpha(colorPalette.monotone[4], 0.3)}`,
      transform: `translate(-50%, -50%) scale(1.5)`,
    }
    const invisibleDefaultHoverStyle = {
      boxShadow: `0 0 0 0 transparent`,
    }
    const { size: hitAreaSize, innerOffset: hitAreaInnerOffset } =
      computeThumbHitArea({
        transparentThumb,
        sliderValue: ownerState.value,
        sliderMin: ownerState.min ?? 0,
        sliderMax: ownerState.max ?? 0,
      })
    const hitAreaShiftX =
      thumb === 0 ? hitAreaInnerOffset : thumb === 1 ? -hitAreaInnerOffset : 0
    const hitAreaStyle = disabled
      ? {}
      : {
          '&::before': {
            content: '""',
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: `translate(calc(-50% + ${hitAreaShiftX}px), -50%)`,
            width: `${hitAreaSize}px`,
            height: `${hitAreaSize}px`,
            borderRadius: '50%',
          },
        }
    const iconVisibleStyle = { opacity: 1 }
    const iconEnlargedStyle = {
      opacity: 1,
      transform: `translate(-50%, -50%) scale(1.3)`,
    }
    const dotSize = disabled ? 0 : transparentThumb ? 14 : 8
    const iconRowHoverStyle =
      isv2 && transparentThumb && !disabled
        ? {
            '.ag-cell:hover & > .thumb-affordance-icon': iconVisibleStyle,
          }
        : {}
    return {
      backgroundColor: 'transparent',
      top: topValue,
      height: `${disabled ? 0 : 20}px`,
      width: `${disabled ? 0 : 20}px`,
      borderRadius: '50%',
      color: transparentThumb ? 'transparent' : sliderColor,
      boxSizing: 'border-box',
      outline: 0,
      pointerEvents: 'all',
      cursor: 'pointer',
      margin: 0,
      zIndex: 4,
      ...hitAreaStyle,
      ...rowHoverThumbStyle,
      ...iconRowHoverStyle,
      '&::after': {
        height: `${dotSize}px`,
        width: `${dotSize}px`,
        borderRadius: '50%',
        transition:
          'background-color 0.1s ease, border 0.1s ease, opacity 0.1s ease, transform 0.1s ease, box-shadow 0.1s ease !important',
        backgroundColor: transparentThumb ? 'transparent' : sliderColor,
      },
      '& > .thumb-affordance-icon': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        opacity: 0,
        transition: 'opacity 0.1s ease, transform 0.1s ease',
        zIndex: 1,
        '& > svg': {
          fontSize: '20px',
          color: alpha(colorPalette.monotone[5], 0.95),
        },
      },
      ':hover': invisibleDefaultHoverStyle,
      '&.Mui-focusVisible': invisibleDefaultHoverStyle,
      '&.Mui-active': invisibleDefaultHoverStyle,
      ':hover::after': hoverStyle,
      '&.Mui-focusVisible::after': hoverStyle,
      '&.Mui-active::after': hoverStyle,
      ':hover > .thumb-affordance-icon': iconEnlargedStyle,
      '&.Mui-focusVisible > .thumb-affordance-icon': iconEnlargedStyle,
      '&.Mui-active > .thumb-affordance-icon': iconEnlargedStyle,
      '& .MuiSlider-valueLabel.MuiSlider-valueLabelOpen': {
        transform: `translateY(${index === 0 ? 40 : -120}%) scale(0.9)`,
      },
      '& .MuiSlider-valueLabel': {
        top: `${index === 0 ? 54 : 0}%`,
        transition: 'all 0.1s ease !important',
        visibility: disabled || labeldisabled ? 'hidden' : undefined,
        zIndex: 10,
      },
    }
  }
)

type SliderThumbComponentProps = React.PropsWithChildren<
  React.ComponentPropsWithoutRef<typeof SliderThumb>
> & {
  'data-index'?: number
  ownerState: StyledSliderProps & { value: number[]; min: number; max: number }
}

export function StyledSliderThumb(props: SliderThumbComponentProps) {
  const { children, ...other } = props
  const thumb = other['data-index']
  const { disablethumbs, value, min, max, isv2 } = props.ownerState
  const isDisabled =
    (thumb === 0 && disablethumbs[0]) || (thumb === 1 && disablethumbs[1])
  return (
    <StyledSliderThumbBase
      {...other}
      thumb={thumb}
      disabled={isDisabled}
      labeldisabled={
        +(
          (thumb === 0 && value[thumb] === min) ||
          (thumb === 1 && value[thumb] === max)
        )
      }
    >
      {children}
      {!isDisabled && !!isv2 && (
        <span className="thumb-affordance-icon">
          {thumb === 0 ? <ArrowLeft /> : <ArrowRight />}
        </span>
      )}
    </StyledSliderThumbBase>
  )
}
